"use client"

import * as React from 'react'
import Link from 'next/link'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { Page, PageBody, PageHeader } from '@open-mercato/ui/backend/Page'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { Button } from '@open-mercato/ui/primitives/button'

type ECmrListItem = {
  id: string
  documentNumber: string
  status: string
  issueDate: string
  referenceNumber: string | null
  senderName: string | null
  carrierName: string | null
  receiverName: string | null
  placeOfTakingOver: string
  placeOfDelivery: string
  currentSealHash: string
}

type ECmrListResponse = {
  items: ECmrListItem[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

async function fetchList(): Promise<ECmrListResponse> {
  return readApiResultOrThrow<ECmrListResponse>('/api/road/ecmr?pageSize=50', undefined, {
    errorMessage: 'Failed to fetch eCMR documents.',
  })
}

export default function ECmrListPage() {
  const t = useT()
  const scopeVersion = useOrganizationScopeVersion()
  const query = useQuery({
    queryKey: ['ecmr', 'list', scopeVersion],
    queryFn: fetchList,
  })

  const columns = React.useMemo<ColumnDef<ECmrListItem>[]>(() => [
    { accessorKey: 'documentNumber', header: t('ecmr.table.documentNumber', 'Document') },
    { accessorKey: 'status', header: t('ecmr.table.status', 'Status') },
    { accessorKey: 'issueDate', header: t('ecmr.table.issueDate', 'Issue date') },
    { accessorKey: 'senderName', header: t('ecmr.table.sender', 'Sender') },
    { accessorKey: 'carrierName', header: t('ecmr.table.carrier', 'Carrier') },
    { accessorKey: 'receiverName', header: t('ecmr.table.receiver', 'Receiver') },
    { accessorKey: 'placeOfTakingOver', header: t('ecmr.table.placeOfTakingOver', 'Taking over') },
    { accessorKey: 'placeOfDelivery', header: t('ecmr.table.placeOfDelivery', 'Delivery') },
  ], [t])

  if (query.isLoading && !query.data) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('ecmr.messages.loading', 'Loading eCMR documents...')} />
        </PageBody>
      </Page>
    )
  }

  if (query.isError || !query.data) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage
            label={t('ecmr.messages.loadErrorTitle', 'Could not load eCMR documents')}
            description={t('ecmr.messages.loadError', 'Try again in a moment or refresh the page.')}
          />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title={t('ecmr.pages.list.title', 'eCMR')}
        description={t('ecmr.pages.list.description', 'Road transport consignment notes and signatures.')}
        actions={(
          <Button asChild>
            <Link href="/backend/ecmr/create">{t('ecmr.actions.create', 'Create eCMR')}</Link>
          </Button>
        )}
      />
      <PageBody>
        <DataTable
          title={t('ecmr.pages.list.tableTitle', 'eCMR documents')}
          data={query.data.items}
          columns={columns}
          rowClickActionIds={['open']}
          rowActions={(row) => (
            <RowActions
              items={[
                { id: 'open', label: t('ecmr.actions.open', 'Open'), href: `/backend/ecmr/${row.id}` },
                { id: 'pdf', label: t('ecmr.actions.pdf', 'PDF'), href: `/api/road/ecmr/${row.id}/pdf` },
              ]}
            />
          )}
        />
      </PageBody>
    </Page>
  )
}
